import { useEffect, useMemo } from 'react';
import { useRouteError } from 'react-router-dom';
import { P2GApp, P2GAppConfiguration } from '@parcel2go/npm-p2g-app';
import ScrollToTop from './scroll';
import { P2GBox, P2GButton, P2GGrid, P2GText } from '@parcel2go/core-ui';
import * as Sentry from '@sentry/react';
import { capturePromiseException } from '~/lib/metrics';
import {
  ANALYTICS_EDGE_API,
  APP,
  CONTENT_API,
  ContentGroups,
  ENVIRONMENT,
  GTM_ENABLED,
  LOCAL_TENANT_OVERRIDE,
  LOCALES_PATH,
  PAYMENT_API,
  QUERY_API,
  CORE_TRANSLATION_NAMESPACES,
  VERSION,
  WSS
} from '~/lib/constants';
import { Component as Settings } from './settings';
import { usePendingSubscriptionPlanCapture } from '~/hooks/use-pending-subscription-plan';

const authProps = {
  scope: 'openid public-api address-search my-profile payment edge authorizer profile email',
  clientId: 'smartsend.cs',
  required: true,
  redirectUri: `${window.location.protocol}//${window.location.host}/ui/statics/myaccount-signin-callback`,
  silentRedirectUri: `${window.location.protocol}//${window.location.host}/ui/statics/myaccount-silent-callback`
};

const baseSettings: P2GAppConfiguration = {
  app: APP,
  appVersion: VERSION,
  contentApi: CONTENT_API,
  environment: ENVIRONMENT,
  gtmEnabled: GTM_ENABLED,
  localesPath: LOCALES_PATH,
  paymentsApi: PAYMENT_API,
  wss: WSS,
  queryApi: QUERY_API,
  contentGroup: ContentGroups.MYACCOUNT,
  translationNamespaces: CORE_TRANSLATION_NAMESPACES,
  tenantOverride: LOCAL_TENANT_OVERRIDE,
  preventMissingKeyUpdates: true
};

export const isRunningAsApp = () => {
  return (
    document.location.href.includes('device=app') ||
    document.location.href.includes('/app/') ||
    navigator['standalone'] === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );
};

export function Component() {
  // Mounted here (rather than per-route) so a tier-specific link is captured regardless of which
  // page the customer first lands on post-sign-in - see use-pending-subscription-plan.ts.
  usePendingSubscriptionPlanCapture();

  const settings: P2GAppConfiguration = useMemo(() => {
    const isApp = isRunningAsApp();
    return {
      ...baseSettings,
      footerVariant: isApp ? 'NONE' : baseSettings.footerVariant,
      headerVariant: isApp ? 'NONE' : baseSettings.headerVariant
    };
  }, []);
  return (
    <ScrollToTop>
      <P2GApp settings={settings} authProps={authProps} analyticsProps={{ endpoint: ANALYTICS_EDGE_API }}>
        <Settings />
      </P2GApp>
    </ScrollToTop>
  );
}

export function AppComponent() {
  return <Settings />;
}

export function ErrorBoundary() {
  const error = useRouteError();

  useEffect(() => {
    if (error) {
      capturePromiseException(error);
    }
  }, [error]);

  return (
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <P2GBox p={6}>
        <P2GGrid container direction="column" alignItems="center" spacing={2}>
          <P2GGrid item>
            <P2GText variant="h3">Oops!</P2GText>
          </P2GGrid>
          <P2GGrid item>
            <P2GText variant="h4" component="p" textAlign="center">
              Something went a bit wrong.
              <br />
              We sincerely apologise for any inconvenience caused.
            </P2GText>
          </P2GGrid>
          <P2GGrid item>
            <P2GButton onClick={() => (window.location.href = '/')}>Back to homepage</P2GButton>
          </P2GGrid>
        </P2GGrid>
      </P2GBox>
    </Sentry.ErrorBoundary>
  );
}

ErrorBoundary.displayName = 'Root Error Boundary';
